/**
 * Auth-v2 web bootstrap hook — closes the "logged out on page
 * refresh" gap.
 *
 * Because the access token is intentionally RAM-only (see
 * `tokens.ts` — module-level variable, never written to
 * `localStorage`), a hard reload always starts with
 * `getAccessToken() === null`. The refresh token lives in the
 * HttpOnly `hero_rt` cookie and cannot be read by JS, so we need a
 * one-shot `POST /auth/refresh` at boot to convert the cookie back
 * into an in-memory access token. Until that request settles, the
 * app has no way to tell "the user really is signed out" apart from
 * "we just haven't traded the cookie for an access token yet".
 *
 * Without this hook, the classic race is:
 *
 * 1. User reloads a `ProtectedRoute` page.
 * 2. `isLoggedIn()` returns `false` (RAM cleared by reload).
 * 3. `ProtectedRoute` synchronously `<Navigate>`s to `/auth/register`.
 * 4. No `/auth/refresh` was ever fired — the SDK's 401 interceptor
 *    only kicks in when an outgoing request lands a 401, and none
 *    went out.
 *
 * This hook plugs the hole:
 *
 * - Apps register the SDK's refresh function once at startup via
 *   {@link registerRefreshHandler} (typically the same
 *   `createWebRefreshHandler(baseURL)` they hand to the SDK).
 * - The hook watches the `hero.hasSession` sentinel written by
 *   {@link setAccessToken}. If the sentinel says "there was a
 *   session here" but RAM is empty, it fires the registered refresh
 *   exactly once and keeps `isBootstrapping = true` until the
 *   request settles.
 * - `useAccessToken()` uses `useSyncExternalStore` on top of
 *   {@link subscribeToAccessToken} so consumers re-render the
 *   moment a token lands (or is cleared).
 *
 * See `apps/backend/src/app/auth/README.md` for the cookie / access
 * token lifecycle on the backend side.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAccessToken, hasResumableSession, subscribeToAccessToken } from './tokens';
import { accessTokenNeedsRefresh } from './jwtSchedule';

type RefreshHandler = () => Promise<string | null>;

let registeredHandler: RefreshHandler | null = null;
let inflightRefresh: Promise<string | null> | null = null;

/**
 * Register the SDK's refresh handler so {@link useAuthBootstrap}
 * can invoke it at boot time. Apps call this once from their
 * `services/api.ts`, right next to the `new CarrierAPI({...})` /
 * `new CustomerAPI({...})` construction.
 */
export function registerRefreshHandler(handler: RefreshHandler): void {
  registeredHandler = handler;
}

/**
 * Invoke the registered refresh handler, de-duplicated across
 * concurrent callers. Returns `null` when no handler is registered
 * or the handler itself returned `null` (i.e. the session is dead).
 */
export function runRegisteredRefresh(): Promise<string | null> {
  if (!registeredHandler) return Promise.resolve(null);
  if (!inflightRefresh) {
    inflightRefresh = registeredHandler().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

function subscribe(callback: () => void): () => void {
  return subscribeToAccessToken(callback);
}

function getSnapshot(): string | null {
  return getAccessToken();
}

function getServerSnapshot(): string | null {
  return null;
}

/**
 * Reactive read of the in-memory access token. Prefer this over the
 * plain {@link getAccessToken} inside React components — it re-renders
 * on every set/clear.
 */
export function useAccessToken(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Reactive `isLoggedIn` — mirrors {@link isLoggedIn} from `tokens.ts`
 * but re-renders on token changes so route guards flip correctly
 * without a page navigation.
 */
export function useIsLoggedIn(): boolean {
  return useAccessToken() !== null;
}

export type AuthBootstrapState = {
  /**
   * `true` while the one-shot boot-time `/auth/refresh` call is in
   * flight. Route guards should render a spinner (not redirect)
   * while this is `true` — otherwise the user is bounced to the
   * login screen on every hard reload.
   */
  isBootstrapping: boolean;
  /** Reactive equivalent of `getAccessToken() !== null`. */
  isLoggedIn: boolean;
};

/**
 * Boot-time auth gate. Route guards (`ProtectedRoute`,
 * `PublicOnlyRoute`) call this and:
 *
 * - render a spinner while `isBootstrapping === true`,
 * - trust `isLoggedIn` afterwards.
 *
 * Safe to call from any number of components — the underlying
 * refresh call is de-duplicated by {@link runRegisteredRefresh}, and
 * the effect only fires on the first mount that sees a resumable
 * session with no access token.
 */
export function useAuthBootstrap(): AuthBootstrapState {
  const isLoggedIn = useIsLoggedIn();
  // On the very first render, if the sentinel says "there was a
  // session" but RAM is empty, assume we're bootstrapping so guards
  // don't flash the login screen.
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (!hasResumableSession()) return false;
    const token = getAccessToken();
    if (!token) return true;
    return accessTokenNeedsRefresh(token);
  });
  const kicked = useRef(false);

  useEffect(() => {
    if (kicked.current) return;
    kicked.current = true;

    // Nothing to resume, or we already have a fresh token — no boot call.
    const token = getAccessToken();
    if (!hasResumableSession() || (token && !accessTokenNeedsRefresh(token))) {
      setIsBootstrapping(false);
      return;
    }

    // If the app never registered a handler, we can't recover the
    // session; drop the "bootstrapping" flag so the login screen
    // renders instead of a permanent spinner.
    if (!registeredHandler) {
      setIsBootstrapping(false);
      return;
    }

    runRegisteredRefresh().finally(() => {
      setIsBootstrapping(false);
    });
  }, []);

  return { isBootstrapping, isLoggedIn };
}
